import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  AtSign,
  Building2,
  Camera,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Copy,
  Globe,
  KeyRound,
  MapPin,
  Mail,
  Briefcase,
  UserCircle,
  Users,
  MessageCircle,
  MessageSquare,
  Music2,
  PlayCircle,
  Send,
  Shield,
  Smartphone,
  Timer,
} from "lucide-react";
import OnboardingRequirementsButton from "../../components/dashboard/OnboardingRequirementsButton";
import SlaJourneyFields from "../../components/dashboard/SlaJourneyFields";
import {
  BUSINESS_TYPES,
  BUSINESS_TYPE_OPTIONS,
  BUSINESS_TYPE_OTHER,
  REGIONS,
} from "../../data/institutionOnboardingGuide";
import Select from "../../components/ui/Select";
import { addInstitutionOpsAssignment } from "../../services/institutionScopeApi";
import { fetchTeamMembers } from "../../services/teamMembersApi";
import PageShell from "../../components/dashboard/PageShell";
import BackNavLink from "../../components/dashboard/BackNavLink";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { USE_MOCK_AUTH } from "../../config/api";
import { addInstitution } from "../../data/institutionsStore";
import { createInstitution } from "../../services/institutionsApi";
import { getReturnTarget, navigateWithReturn } from "../../utils/navigationReturn";
import { isValidGhanaPhone } from "../../utils/ghanaPhone";
import { readImageAsDataUrl } from "../../utils/readImageAsDataUrl";
import { showError } from "../../utils/toast";

const STEPS = [
  { id: "profile", label: "Profile", icon: Building2 },
  { id: "access", label: "Access", icon: KeyRound },
  { id: "sla", label: "SLA journey", icon: Timer },
  { id: "review", label: "Launch", icon: Send },
];

const OUTREACH_CHANNELS = [
  {
    key: "sms",
    label: "SMS",
    icon: MessageSquare,
    description: "Payment reminders and due-date alerts via text.",
    tone: "border-sky-200/80 bg-sky-50/60 text-sky-900",
    activeTone: "border-sky-400 bg-sky-50 ring-2 ring-sky-400/25",
    iconTone: "bg-sky-100 text-sky-700",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    icon: MessageCircle,
    description: "Two-way chat, payment links and confirmations.",
    tone: "border-emerald-200/80 bg-emerald-50/60 text-emerald-900",
    activeTone: "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-400/25",
    iconTone: "bg-emerald-100 text-emerald-700",
  },
  {
    key: "email",
    label: "Email",
    icon: Mail,
    description: "Formal notices, statements and correspondence.",
    tone: "border-violet-200/80 bg-violet-50/60 text-violet-900",
    activeTone: "border-violet-400 bg-violet-50 ring-2 ring-violet-400/25",
    iconTone: "bg-violet-100 text-violet-700",
  },
];

const SOCIAL_FIELDS = [
  {
    key: "instagram",
    label: "Instagram",
    icon: Camera,
    placeholder: "@handle",
    tone: "bg-gradient-to-br from-amber-100 to-rose-100 text-rose-700",
  },
  {
    key: "x",
    label: "X",
    icon: AtSign,
    placeholder: "@handle",
    tone: "bg-slate-900 text-white",
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: Globe,
    placeholder: "page or @handle",
    tone: "bg-blue-100 text-blue-700",
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: Music2,
    placeholder: "@handle",
    tone: "bg-zinc-900 text-white",
  },
  {
    key: "youtube",
    label: "YouTube",
    icon: PlayCircle,
    placeholder: "@channel",
    tone: "bg-red-100 text-red-700",
  },
];

function FieldGuide({ title, children }) {
  return (
    <div className="rounded-xl border border-primary/[0.08] bg-primary/[0.02] px-4 py-3">
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">
        <CircleHelp className="h-3.5 w-3.5" />
        {title}
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-primary/55">{children}</p>
    </div>
  );
}

function StepIndicator({ currentStep }) {
  return (
    <div className="mb-6 rounded-xl border border-primary/[0.07] bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between gap-1 overflow-x-auto">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isComplete = index < currentStep;
          return (
            <div key={step.id} className="flex min-w-0 flex-1 items-center gap-1">
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  isComplete
                    ? "bg-primary text-white"
                    : isActive
                      ? "bg-accent text-primary shadow-md shadow-accent/30"
                      : "bg-primary/[0.06] text-primary/35"
                }`}
              >
                {isComplete ? <Check className="h-4 w-4" strokeWidth={3} /> : <Icon className="h-4 w-4" />}
              </div>
              <span
                className={`hidden truncate text-xs font-bold sm:block ${
                  isActive || isComplete ? "text-primary" : "text-primary/35"
                }`}
              >
                {step.label}
              </span>
              {index < STEPS.length - 1 && (
                <div
                  className={`mx-1 h-0.5 min-w-[8px] flex-1 rounded-full ${
                    index < currentStep ? "bg-primary/25" : "bg-primary/[0.06]"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const initialForm = {
  legal_name: "",
  business_type: "Hospital",
  status: "active",
  region: "Greater Accra",
  city: "",
  gps_address: "",
  location: "",
  primary_email: "",
  primary_phone_number: "",
  secondary_email: "",
  secondary_phone_number: "",
  website: "",
  logo: null,
  channels: { sms: true, whatsapp: true, email: true },
  socials: {
    instagram: "",
    x: "",
    facebook: "",
    tiktok: "",
    youtube: "",
  },
  contract_sla_days: 20,
  manager_assignment_days: 5,
  agent_engagement_days: 2,
  ops_manager_id: "",
};

export default function OnboardInstitutionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const institutionsFallback = getReturnTarget(searchParams, "/dashboard/institutions", location.state);
  const [step, setStep] = useState(0);
  const [opsTeam, setOpsTeam] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState("");

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setSocial = (key, value) =>
    setForm((prev) => ({ ...prev, socials: { ...prev.socials, [key]: value } }));
  const setChannel = (key, value) =>
    setForm((prev) => ({ ...prev, channels: { ...prev.channels, [key]: value } }));

  const handleLogoPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await readImageAsDataUrl(file);
      set("logo", dataUrl);
    } catch (err) {
      showError(err.message || "Could not read logo.");
    }
  };

  const institutionLabel = form.legal_name.trim() || "This institution";
  const businessTypeSelectValue = BUSINESS_TYPES.includes(form.business_type)
    ? form.business_type
    : BUSINESS_TYPE_OTHER;

  useEffect(() => {
    if (!useLiveApi || step !== 2) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const members = await fetchTeamMembers(token);
        if (cancelled) return;
        const eligible = (members || []).filter((m) => {
          if (m.status === "deleted" || m.status === "inactive") return false;
          return String(m.role || m.role_slug || "")
            .toLowerCase()
            .replace(/[\s-]+/g, "_") === "call_center_manager";
        });
        setOpsTeam(eligible);
      } catch {
        if (!cancelled) setOpsTeam([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, token, useLiveApi]);

  const opsManagerOptions = useMemo(
    () => [
      { value: "", label: "Assign later on institution page…" },
      ...opsTeam.map((m) => ({
        value: String(m.id || ""),
        label: m.name || m.email || m.id,
      })),
    ],
    [opsTeam]
  );

  const slaValues = useMemo(
    () => ({
      contract_sla_days: form.contract_sla_days,
      manager_assignment_days: form.manager_assignment_days,
      agent_engagement_days: form.agent_engagement_days,
    }),
    [form.contract_sla_days, form.manager_assignment_days, form.agent_engagement_days]
  );

  const canAdvance = () => {
    if (step === 0) {
      return form.legal_name.trim() && form.business_type.trim() && form.region;
    }
    if (step === 1) {
      return form.primary_email.includes("@") && isValidGhanaPhone(form.primary_phone_number);
    }
    if (step === 2) {
      return (
        Number(form.contract_sla_days) > 0 &&
        Number(form.manager_assignment_days) > 0 &&
        Number(form.agent_engagement_days) > 0
      );
    }
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const institution = await createInstitution(useLiveApi ? token : null, form);
      addInstitution(institution);

      const institutionId = institution.id || institution.provider_id;
      if (useLiveApi && form.ops_manager_id && institutionId) {
        try {
          await addInstitutionOpsAssignment(token, institutionId, form.ops_manager_id);
        } catch (assignErr) {
          showError(assignErr.message || "Institution created but ops assignment failed.");
        }
      }

      const adminEmail = institution.primary_email || form.primary_email;
      const adminPhone = institution.primary_phone_number || form.primary_phone_number;

      setCreated({
        institution,
        portalUser: {
          email: adminEmail,
          phone: adminPhone,
        },
        invite: {
          email: adminEmail,
          phone: adminPhone,
          portalUrl: "https://provider.simcollect.com/login",
          authMethod: "passwordless_otp",
        },
      });
      setSubmitted(true);
    } catch (err) {
      showError(err.message || "Could not onboard institution.");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const backLink = (
    <BackNavLink fallback="/dashboard/institutions" fallbackLabel="All institutions" />
  );

  if (submitted && created) {
    const displayName = created.institution.name || created.institution.legal_name || institutionLabel;
    return (
      <PageShell title="Institution onboarded" description="Welcome email sent to the portal login address.">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-b from-emerald-50/90 to-white p-6 text-center shadow-[0_8px_32px_rgba(5,150,105,0.08)] sm:p-8">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h2 className="mt-4 text-xl font-bold text-primary">{displayName} is live</h2>
            <p className="mt-2 text-sm text-primary/55">
              A welcome email was sent to{" "}
              <span className="font-semibold text-primary">{created.portalUser.email}</span>.
            </p>
            <p className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-xs text-primary/60 ring-1 ring-emerald-600/10">
              Institution code{" "}
              <span className="font-semibold text-primary">{created.institution.code || "—"}</span>
              {" · "}
              portal login is the primary email (OTP)
            </p>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-primary/[0.08] bg-white shadow-[0_4px_24px_rgba(127,29,29,0.06)]">
            <div className="border-b border-primary/[0.06] bg-gradient-to-r from-primary/[0.05] to-accent/[0.08] px-5 py-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary/45">
                Portal login credentials
              </p>
              <p className="mt-1 text-sm font-semibold text-primary">
                {displayName}
                {created.institution.code ? ` · ${created.institution.code}` : ""}
              </p>
            </div>
            <div className="space-y-4 p-5">
              <CredentialRow
                label="Portal URL"
                value={created.invite.portalUrl}
                copied={copied === "url"}
                onCopy={() => copyText(created.invite.portalUrl, "url")}
              />
              <CredentialRow
                label="Login email (primary)"
                value={created.invite.email}
                copied={copied === "email"}
                onCopy={() => copyText(created.invite.email, "email")}
              />
              <CredentialRow
                label="Contact phone"
                value={created.invite.phone}
                copied={copied === "phone"}
                onCopy={() => copyText(created.invite.phone, "phone")}
              />
            </div>
            <div className="border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-3">
              <p className="flex items-center gap-2 text-xs text-primary/45">
                <Shield className="h-3.5 w-3.5" />
                Passwordless login — OTP sent to the primary email on sign-in.
              </p>
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-primary/[0.08] bg-white p-5 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">
              SLA journey (active settings)
            </p>
            <div className="mt-3">
              <SlaJourneyFields
                readOnly
                values={{
                  contract_sla_days:
                    created.institution.contract_sla_days ??
                    created.institution.sla_days ??
                    form.contract_sla_days,
                  manager_assignment_days:
                    created.institution.manager_assignment_days ?? form.manager_assignment_days,
                  agent_engagement_days:
                    created.institution.agent_engagement_days ?? form.agent_engagement_days,
                }}
                institutionName={displayName}
                onChange={() => {}}
              />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                navigateWithReturn(navigate, `/dashboard/institutions/${created.institution.id}`, location);
              }}
            >
              View institution
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(institutionsFallback)}>
              Back to list
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Onboard institution"
      description="Register a service provider client. Institution code is generated automatically."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <OnboardingRequirementsButton />
          {backLink}
        </div>
      }
    >
      <StepIndicator currentStep={step} />

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 14 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -14 }}
          transition={{ duration: 0.2 }}
          className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] sm:p-6"
        >
          {step === 0 && (
            <>
              <h2 className="text-sm font-bold text-primary">Institution profile</h2>
              <p className="mt-0.5 text-xs text-primary/45">Legal identity and location.</p>
              <FieldGuide title="Code">
                A unique institution code is generated on the server when you launch. Team members invited
                later share that code.
              </FieldGuide>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Input
                  label="Legal name"
                  value={form.legal_name}
                  onChange={(e) => set("legal_name", e.target.value)}
                  placeholder="e.g. Green Valley Hospital"
                  required
                />
                <div>
                  <Select
                    label="Business type"
                    value={businessTypeSelectValue}
                    onChange={(value) => {
                      if (value === BUSINESS_TYPE_OTHER) {
                        set(
                          "business_type",
                          BUSINESS_TYPES.includes(form.business_type) ? "" : form.business_type
                        );
                        return;
                      }
                      set("business_type", value);
                    }}
                    options={BUSINESS_TYPE_OPTIONS}
                  />
                  {businessTypeSelectValue === BUSINESS_TYPE_OTHER ? (
                    <Input
                      containerClassName="mt-3"
                      label="Specify business type"
                      value={form.business_type}
                      onChange={(e) => set("business_type", e.target.value)}
                      placeholder="e.g. Insurance, Fintech, Telecom"
                      required
                    />
                  ) : null}
                </div>
                <Select
                  label="Region"
                  value={form.region}
                  onChange={(value) => set("region", value)}
                  options={REGIONS.map((r) => ({ value: r, label: r }))}
                />
                <Input
                  label="City"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="e.g. Accra"
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Location"
                    value={form.location}
                    onChange={(e) => set("location", e.target.value)}
                    placeholder="Area or landmark"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    label="GPS / physical address"
                    value={form.gps_address}
                    onChange={(e) => set("gps_address", e.target.value)}
                    placeholder="Street, landmark, or GPS pin"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    label="Website"
                    value={form.website}
                    onChange={(e) => set("website", e.target.value)}
                    placeholder="https://"
                    icon={Globe}
                  />
                </div>
                <div className="sm:col-span-2">
                  <p className="mb-1.5 text-xs font-semibold text-primary/50">Institution logo</p>
                  <div className="flex flex-wrap items-center gap-4 rounded-xl border border-primary/10 bg-primary/[0.02] p-4">
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-primary/10">
                      {form.logo ? (
                        <img src={form.logo} alt="" className="h-full w-full object-contain" />
                      ) : (
                        <Building2 className="h-7 w-7 text-primary/30" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-primary/50">
                        Optional. PNG, JPG, or WebP up to 2 MB. Shown on the provider portal.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-primary/15 bg-white px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/[0.03]">
                          <Camera className="h-3.5 w-3.5" />
                          {form.logo ? "Change logo" : "Upload logo"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="sr-only"
                            onChange={handleLogoPick}
                          />
                        </label>
                        {form.logo ? (
                          <button
                            type="button"
                            onClick={() => set("logo", null)}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-primary/50 hover:bg-primary/[0.04] hover:text-primary"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="text-sm font-bold text-primary">Portal login & contact</h2>
              <p className="mt-0.5 text-xs text-primary/45">
                Portal sign-in uses the primary email only. Phone numbers are contact details.
              </p>

              <div className="mt-4 rounded-xl border border-primary/[0.08] bg-primary/[0.02] p-4">
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">
                  <KeyRound className="h-3.5 w-3.5" />
                  Portal login
                </p>
                <p className="mt-1 text-xs text-primary/50">
                  The provider portal signs in with this email (OTP). A welcome email is sent here on launch.
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Primary email"
                    type="email"
                    value={form.primary_email}
                    onChange={(e) => set("primary_email", e.target.value)}
                    icon={Mail}
                    required
                  />
                  <Input
                    label="Primary phone (contact)"
                    type="tel"
                    placeholder="024XXXXXXX"
                    value={form.primary_phone_number}
                    onChange={(e) => set("primary_phone_number", e.target.value)}
                    icon={Smartphone}
                    required
                  />
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-primary/[0.08] p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">
                  Contact details
                </p>
                <p className="mt-1 text-xs text-primary/50">
                  Optional business contact — not used for portal login.
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Secondary email"
                    type="email"
                    value={form.secondary_email}
                    onChange={(e) => set("secondary_email", e.target.value)}
                    icon={Mail}
                  />
                  <Input
                    label="Secondary phone"
                    type="tel"
                    placeholder="024XXXXXXX"
                    value={form.secondary_phone_number}
                    onChange={(e) => set("secondary_phone_number", e.target.value)}
                    icon={Smartphone}
                  />
                </div>
              </div>

              <p className="mt-5 text-sm font-medium text-primary/70">Outreach channels</p>
              <p className="mt-0.5 text-xs text-primary/45">
                Select how SimCollect may reach debtors for {institutionLabel}.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {OUTREACH_CHANNELS.map(({ key, label, icon: Icon, description, tone, activeTone, iconTone }) => {
                  const selected = form.channels[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setChannel(key, !selected)}
                      className={`flex h-full flex-col rounded-2xl border p-4 text-left transition-all ${
                        selected ? activeTone : `${tone} opacity-90 hover:opacity-100`
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconTone}`}>
                          <Icon className="h-5 w-5" strokeWidth={2.25} />
                        </span>
                        {selected && (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white shadow-sm">
                            <Check className="h-3.5 w-3.5" strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <p className="mt-3 text-sm font-bold text-primary">{label}</p>
                      <p className="mt-1.5 flex-1 text-xs leading-relaxed text-primary/55">{description}</p>
                      <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                        {selected ? "Enabled" : "Tap to enable"}
                      </p>
                    </button>
                  );
                })}
              </div>

              <p className="mt-5 text-sm font-medium text-primary/70">Social handles (optional)</p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                {SOCIAL_FIELDS.map(({ key, label, icon: Icon, placeholder, tone }) => (
                  <div key={key} className="flex items-start gap-3">
                    <span
                      className={`mt-6 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}
                      aria-hidden
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <Input
                        label={label}
                        value={form.socials[key]}
                        onChange={(e) => setSocial(key, e.target.value)}
                        placeholder={placeholder}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-sm font-bold text-primary">SLA journey & ops scope</h2>
              <p className="mt-0.5 text-xs text-primary/45">
                Enter the total SLA days you collected, then decide how many of those days the call centre
                manager has to assign cases and how many the agent has to make first contact. You can also
                link a call centre manager now.
              </p>
              <div className="mt-4">
                <SlaJourneyFields
                  values={slaValues}
                  onChange={(next) =>
                    setForm((prev) => ({
                      ...prev,
                      contract_sla_days: next.contract_sla_days,
                      manager_assignment_days: next.manager_assignment_days,
                      agent_engagement_days: next.agent_engagement_days,
                    }))
                  }
                  institutionName={institutionLabel}
                />
              </div>
              {useLiveApi ? (
                <div className="mt-5 max-w-md">
                  <Select
                    label="Ops manager / CE (optional)"
                    value={form.ops_manager_id}
                    onChange={(value) => set("ops_manager_id", value)}
                    options={opsManagerOptions}
                  />
                </div>
              ) : null}
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-sm font-bold text-primary">Review & launch</h2>
              <p className="mt-0.5 text-xs text-primary/45">
                Confirm branding, access, and SLA before the backend generates your institution code and
                sends the welcome email.
              </p>

              <div className="mt-5 overflow-hidden rounded-2xl border border-primary/[0.08] bg-gradient-to-br from-white via-white to-accent/[0.06] shadow-sm">
                <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
                  <span className="mx-auto flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-md ring-2 ring-accent/20 sm:mx-0">
                    {form.logo ? (
                      <img src={form.logo} alt="" className="h-full w-full object-contain p-1" />
                    ) : (
                      <Building2 className="h-10 w-10 text-primary/25" strokeWidth={1.5} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1 text-center sm:text-left">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-accent-dark/80">
                      Ready to launch
                    </p>
                    <h3 className="mt-1 text-lg font-bold text-primary">{form.legal_name || "Institution"}</h3>
                    <div className="mt-2 flex flex-wrap justify-center gap-2 sm:justify-start">
                      {form.business_type ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/[0.06] px-2.5 py-1 text-xs font-semibold text-primary">
                          <Briefcase className="h-3.5 w-3.5 text-accent" />
                          {form.business_type}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Active on launch
                      </span>
                    </div>
                    {(form.region || form.city) && (
                      <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary/55">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/40" />
                        {[form.region, form.city].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <ReviewSection title="Profile & location" icon={Building2}>
                  <ReviewRow icon={Building2} label="Legal name" value={form.legal_name} />
                  <ReviewRow icon={Briefcase} label="Business type" value={form.business_type} />
                  <ReviewRow
                    icon={MapPin}
                    label="Location"
                    value={[form.location, form.gps_address, form.region, form.city].filter(Boolean).join(" · ")}
                  />
                  {form.website ? (
                    <ReviewRow icon={Globe} label="Website" value={form.website} />
                  ) : null}
                </ReviewSection>

                <ReviewSection title="Portal access" icon={KeyRound}>
                  <ReviewRow icon={Mail} label="Login email" value={form.primary_email} />
                  <ReviewRow icon={Smartphone} label="Login phone" value={form.primary_phone_number} />
                  <ReviewRow icon={AtSign} label="Contact email" value={form.secondary_email} />
                  <ReviewRow icon={Smartphone} label="Contact phone" value={form.secondary_phone_number} />
                </ReviewSection>
              </div>

              <ReviewSection className="mt-4" title="Outreach channels" icon={MessageSquare}>
                <div className="flex flex-wrap gap-2 px-4 pb-4 pt-1">
                  {OUTREACH_CHANNELS.map(({ key, label, icon: Icon, iconTone, activeTone, tone }) => {
                    const on = form.channels[key];
                    return (
                      <span
                        key={key}
                        className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${
                          on ? activeTone : `${tone} opacity-50 grayscale`
                        }`}
                      >
                        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${iconTone}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        {label}
                        {on ? <Check className="h-3.5 w-3.5" /> : null}
                      </span>
                    );
                  })}
                </div>
              </ReviewSection>

              {Object.values(form.socials || {}).some((v) => String(v || "").trim()) ? (
                <ReviewSection className="mt-4" title="Social presence" icon={AtSign}>
                  <div className="grid gap-2 px-4 pb-4 pt-1 sm:grid-cols-2">
                    {SOCIAL_FIELDS.map(({ key, label, icon: Icon, tone }) => {
                      const val = String(form.socials[key] || "").trim();
                      if (!val) return null;
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-3 rounded-xl border border-primary/[0.08] bg-white px-3 py-2.5"
                        >
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-primary/40">{label}</p>
                            <p className="truncate text-sm font-semibold text-primary">{val}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ReviewSection>
              ) : null}

              <div className="mt-4">
                <ReviewSection title="SLA journey" icon={Timer}>
                  <div className="px-4 pb-4 pt-1">
                    <SlaJourneyFields
                      readOnly
                      values={slaValues}
                      institutionName={institutionLabel}
                      onChange={() => {}}
                    />
                  </div>
                </ReviewSection>
              </div>

              <ReviewSection className="mt-4" title="Ops scope" icon={Users}>
                <ReviewRow
                  icon={UserCircle}
                  label="Call centre manager"
                  value={
                    form.ops_manager_id
                      ? opsTeam.find((m) => String(m.id) === String(form.ops_manager_id))?.name ||
                        form.ops_manager_id
                      : "Assign later on institution page"
                  }
                />
              </ReviewSection>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/[0.07] bg-white px-5 py-4">
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => (step === 0 ? navigate(institutionsFallback) : setStep((s) => s - 1))}
        >
          <ChevronLeft className="h-4 w-4" />
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button variant="primary" size="sm" type="button" disabled={!canAdvance()} onClick={() => setStep((s) => s + 1)}>
            Continue
            <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="accent" size="sm" type="button" loading={loading} onClick={handleSubmit}>
            Launch institution
          </Button>
        )}
      </div>
    </PageShell>
  );
}

function ReviewSection({ title, icon: Icon, children, className = "" }) {
  return (
    <section
      className={`overflow-hidden rounded-xl border border-primary/[0.08] bg-white shadow-[0_1px_2px_rgba(127,29,29,0.04)] ${className}`}
    >
      <div className="flex items-center gap-2 border-b border-primary/[0.06] bg-primary/[0.02] px-4 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/15 text-accent">
          <Icon className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-primary/70">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function ReviewRow({ icon: Icon, label, value }) {
  const display = value && String(value).trim() ? String(value) : "—";
  return (
    <div className="flex items-start gap-3 border-t border-primary/[0.05] px-4 py-3 first:border-t-0">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/[0.04] text-primary/45">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">{label}</p>
        <p className="mt-0.5 break-words text-sm font-semibold text-primary">{display}</p>
      </div>
    </div>
  );
}

function CredentialRow({ label, value, copied, onCopy, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-primary/40">{label}</p>
        <p className={`mt-0.5 truncate text-sm font-semibold text-primary ${mono ? "font-mono" : ""}`}>{value}</p>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-xs font-bold text-primary ring-1 ring-primary/10 hover:bg-primary/[0.03]"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
